import { authConfigsClient } from '~/services';
import { ref, watch, computed } from 'vue';
import { createGlobalState } from '@vueuse/core';

const emptyHeader = () => ({ name: '', value: '' });

const defaultData = () => ({
    auth_config_type: 'custom_header',
    friendly_name: '',
    headers: [emptyHeader()],
});

const stripBlankHeaders = (headers) => headers.filter((h) => h.name !== '' || h.value !== '');

const headersAreEqual = (a, b) => {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i].name !== b[i].name || a[i].value !== b[i].value) {
            return false;
        }
    }
    return true;
};

export const useAuthConfigForm = createGlobalState(() => {
    const teamId = Spark.state.currentTeam.id;

    const originalData = ref(defaultData());
    const data = ref(defaultData());
    const success = ref(false);
    const submitting = ref(false);
    const loading = ref(false);

    watch(
        () => data.value?.auth_config_id,
        async (id) => {
            success.value = false;
            submitting.value = false;
            loading.value = true;

            if (id) {
                const resp = (await authConfigsClient.get(teamId, id)).data;
                // The API never returns header values (secrets); rebuild rows with blank values.
                const serverHeaders =
                    Array.isArray(resp.headers) && resp.headers.length > 0
                        ? resp.headers.map((h) => ({ name: h.name, value: '' }))
                        : [emptyHeader()];
                originalData.value = {
                    ...resp,
                    headers: serverHeaders,
                };
                data.value = {
                    ...originalData.value,
                    headers: serverHeaders.map((h) => ({ ...h })),
                };
            } else {
                originalData.value = defaultData();
                data.value = defaultData();
            }
            loading.value = false;
        },
        { immediate: true }
    );

    const unmount = () => {
        originalData.value = defaultData();
        data.value = defaultData();
        success.value = false;
        submitting.value = false;
        loading.value = false;
    };

    const payload = computed(() => {
        const params = {
            auth_config_type: data.value.auth_config_type,
            friendly_name: data.value.friendly_name,
        };

        if (data.value.auth_config_type === 'custom_header') {
            const cleaned = stripBlankHeaders(data.value.headers);

            if (data.value.auth_config_id) {
                if (!headersAreEqual(cleaned, originalData.value.headers ?? [])) {
                    params.headers = cleaned;
                }
            } else {
                params.headers = cleaned;
            }
        } else if (data.value.auth_config_type === 'oauth2_client_credentials') {
            if (data.value.auth_config_id) {
                const changed =
                    data.value.token_endpoint !== originalData.value.token_endpoint ||
                    data.value.client_id !== originalData.value.client_id ||
                    data.value.client_secret !== originalData.value.client_secret;
                if (changed) {
                    params.token_endpoint = data.value.token_endpoint;
                    params.client_id = data.value.client_id;
                    params.client_secret = data.value.client_secret;
                }
            } else {
                params.token_endpoint = data.value.token_endpoint;
                params.client_id = data.value.client_id;
                params.client_secret = data.value.client_secret;
            }
        }

        return params;
    });

    const submit = async () => {
        if (submitting.value) {
            return;
        }

        submitting.value = true;

        try {
            if (data.value.auth_config_id) {
                const response = (await authConfigsClient.update(teamId, data.value.auth_config_id, payload.value))
                    .data;
                success.value = true;

                return response;
            } else {
                const response = (await authConfigsClient.create(teamId, payload.value)).data;
                success.value = true;

                return response;
            }
        } catch (e) {
            success.value = false;
            throw e;
        } finally {
            submitting.value = false;
        }
    };

    return { submitting, loading, submit, data, payload, success, unmount, defaultData };
});
